// Booking rules + waitlist quote buttons (Kelly, 2026-10-24). Business
// settings, owner-only (migration-2026-10-24-booking-rules.sql). Defaults keep
// the old fixed behaviour (7am-10pm every day, 30-minute slots, 90-minute
// hold, 5-45 minute quotes) until the owner changes them in Settings > Home
// screen. The forms still take any time, party size or wait as a one-off.

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const SLOT_CHOICES = [15, 30, 60];
export const HOLD_CHOICES = [45, 60, 90, 120, 150, 180];

export const DEFAULT_BOOKING_RULES = {
  hours: DAY_NAMES.map(() => ({ on: true, from: '07:00', to: '22:00' })),
  slotMin: 30,
  holdMin: 90,
  bigPartySize: null,     // null = every party gets holdMin
  bigPartyHoldMin: 120,
};
export const DEFAULT_WAITLIST_QUOTES = [5, 10, 15, 20, 30, 45];
export const DEFAULT_PARTY_SIZES = [1, 2, 3, 4, 5, 6, 8, 10];

const clampInt = (v, lo, hi, def) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= lo && n <= hi ? n : def;
};
const cleanTime = (v, def) => (/^([01]\d|2[0-3]):[0-5]\d$/.test(String(v ?? '')) ? String(v) : def);

export const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + (m || 0);
};
export const fromMinutes = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

// Accepts the stored object, its JSON text (localStorage cache) or junk.
export function parseBookingRules(v) {
  let raw = v;
  if (typeof v === 'string') { try { raw = JSON.parse(v); } catch { raw = null; } }
  const d = DEFAULT_BOOKING_RULES;
  if (!raw || typeof raw !== 'object') return structuredClone(d);
  const hours = DAY_NAMES.map((_, i) => {
    const h = Array.isArray(raw.hours) ? raw.hours[i] : null;
    return {
      on: h?.on !== false,
      from: cleanTime(h?.from, d.hours[i].from),
      to: cleanTime(h?.to, d.hours[i].to),
    };
  });
  const big = raw.bigPartySize == null ? null : clampInt(raw.bigPartySize, 2, 99, null);
  return {
    hours,
    slotMin: SLOT_CHOICES.includes(Number(raw.slotMin)) ? Number(raw.slotMin) : d.slotMin,
    holdMin: clampInt(raw.holdMin, 15, 600, d.holdMin),
    bigPartySize: big,
    bigPartyHoldMin: clampInt(raw.bigPartyHoldMin, 15, 600, d.bigPartyHoldMin),
  };
}

export function parseWaitlistQuotes(v) {
  const list = (Array.isArray(v) ? v : String(v ?? '').split(','))
    .map((n) => Math.round(Number(n)))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 600);
  const unique = [...new Set(list)].sort((a, b) => a - b).slice(0, 8);
  return unique.length ? unique : [...DEFAULT_WAITLIST_QUOTES];
}

// Booking start times ("H:MM", same format the form stores) for a weekday
// (0 = Sunday, in the business's calendar). Closing time is the last time a
// booking can START.
export function slotsForDay(rules, weekday) {
  const day = rules.hours[weekday];
  if (!day?.on) return [];
  const from = toMinutes(day.from), to = toMinutes(day.to);
  const out = [];
  for (let m = from; m <= to; m += rules.slotMin) out.push(`${Math.floor(m / 60)}:${m % 60}`);
  return out;
}

// How long a booking holds its table by default.
export const holdFor = (rules, partySize) =>
  (rules.bigPartySize && partySize >= rules.bigPartySize ? rules.bigPartyHoldMin : rules.holdMin);

// "1 hr 30 min" style label for a number of minutes.
export const durationLabel = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr${h > 1 ? 's' : ''}`;
};
